import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Divider,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Button,
  Alert,
} from '@mui/material';
import config from '../config';
import { FaChartLine, FaServer, FaList, FaLink, FaTimes, FaFilePdf, FaUsers } from 'react-icons/fa';
const server_URL = config.SERVER_URL;

const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (value === 0) {
      setCount(0);
      return;
    }
    let startTime = null;
    const startValue = 0;
    const endValue = value;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutQuart);
      setCount(currentCount);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  return <span>{count.toLocaleString()}</span>;
};

const CHART_TIMEZONE = 'Asia/Kolkata';

// Use the stored calendar date for IST wall-clock timestamps so 12:01 AM
// stays on that day. Only convert when the value is clearly UTC.
const getDateKey = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return value.toLocaleDateString('en-CA', { timeZone: CHART_TIMEZONE });
  }
  const s = String(value);
  if (/Z|[+-]00:00/i.test(s)) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-CA', { timeZone: CHART_TIMEZONE });
  }
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

const addUtcDays = (date, days) => {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const keyToUtcDate = (key) => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatChartLabel = (key) => {
  const [year, month, day] = String(key).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatOpenedAt = (email, formatDate) => {
  if (!email?.opened_at) return '-';
  if (email.last_opened_at && email.last_opened_at !== email.opened_at) {
    return `${formatDate(email.opened_at)} (last ${formatDate(email.last_opened_at)})`;
  }
  return formatDate(email.opened_at);
};

const withOpenTimes = (details) => {
  const rows = details?.send_timeline || [];
  if (!rows.length) return rows;
  if (rows.some((row) => row.opened_at)) return rows;
  const byEmail = {};
  (details.open_timeline || []).forEach((event) => {
    if (!event.email || !event.opened_at) return;
    (byEmail[event.email] ||= []).push(event.opened_at);
  });
  Object.values(byEmail).forEach((times) => times.sort());
  return rows.map((row) => {
    const times = byEmail[row.email] || [];
    if (!times.length) return row;
    return {
      ...row,
      opened: true,
      opened_count: row.opened_count || times.length,
      opened_at: times[0],
      last_opened_at: times[times.length - 1],
    };
  });
};

// Prefer server-built IST daily buckets so midnight opens are not shifted to UTC.
const buildDailySeries = (details) => {
  if (details?.daily_series?.labels?.length) {
    return details.daily_series;
  }
  if (!details) {
    return { labels: [], sent: [], opened: [], clicks: [], uniqueClicks: [] };
  }

  // Determine the full activity window so older campaigns are still plotted
  const timestamps = [];
  if (details.campaign?.created_at) timestamps.push(new Date(details.campaign.created_at).getTime());
  (details.send_timeline || []).forEach((item) => {
    const t = item.sent_at ? new Date(item.sent_at).getTime() : null;
    if (t) timestamps.push(t);
  });
  (details.open_timeline || []).forEach((item) => {
    const t = item.opened_at ? new Date(item.opened_at).getTime() : null;
    if (t) timestamps.push(t);
  });
  (details.click_timeline || []).forEach((group) => {
    (group.clicks || []).forEach((click) => {
      const t = click.clicked_at ? new Date(click.clicked_at).getTime() : null;
      if (t) timestamps.push(t);
    });
  });

  if (timestamps.length === 0) {
    return { labels: [], sent: [], opened: [], clicks: [], uniqueClicks: [] };
  }

  const startKey = getDateKey(new Date(Math.min(...timestamps)));
  const endKey = getDateKey(new Date(Math.max(...timestamps)));
  if (!startKey || !endKey) {
    return { labels: [], sent: [], opened: [], clicks: [], uniqueClicks: [] };
  }

  // Build label list from earliest activity to latest (inclusive), most recent 90 days
  const labels = [];
  const maxDays = 90;
  let cursor = keyToUtcDate(startKey);
  const endUTC = keyToUtcDate(endKey);
  const totalDays = Math.round((endUTC - cursor) / 86400000) + 1;
  if (totalDays > maxDays) {
    cursor = addUtcDays(endUTC, -(maxDays - 1));
  }
  let guard = 0;
  while (cursor <= endUTC && guard < maxDays) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    const d = String(cursor.getUTCDate()).padStart(2, '0');
    labels.push(`${y}-${m}-${d}`);
    cursor = addUtcDays(cursor, 1);
    guard += 1;
  }

  // Initialize count maps
  const sentMap = {};
  const openedMap = {};
  const clicksMap = {};
  const uniqueEmailsByDay = {};
  const uniqueOpenersByDay = {};
  for (const label of labels) {
    sentMap[label] = 0;
    openedMap[label] = 0;
    clicksMap[label] = 0;
    uniqueEmailsByDay[label] = new Set();
    uniqueOpenersByDay[label] = new Set();
  }

  // Aggregate send timeline by date
  (details.send_timeline || []).forEach((item) => {
    const key = getDateKey(item.sent_at);
    if (key && key in sentMap) {
      sentMap[key] += 1;
    }
  });

  // Plot opens on the day the mail was opened, not the send day.
  const openEvents = details.open_timeline || [];
  if (openEvents.length > 0) {
    openEvents.forEach((item) => {
      const key = getDateKey(item.opened_at);
      if (!key || !(key in openedMap)) return;
      const identity = item.email || item.uid;
      if (identity) {
        uniqueOpenersByDay[key].add(identity);
      } else {
        openedMap[key] += 1;
      }
    });
    labels.forEach((label) => {
      openedMap[label] += uniqueOpenersByDay[label].size;
    });
  } else {
    (details.send_timeline || []).forEach((item) => {
      const key = getDateKey(item.sent_at);
      if (key && key in openedMap && item.opened) {
        openedMap[key] += 1;
      }
    });
  }

  // Aggregate clicks (click_timeline is grouped by URL with nested clicks)
  (details.click_timeline || []).forEach((group) => {
    (group.clicks || []).forEach((click) => {
      const k = getDateKey(click.clicked_at);
      if (!k || !(k in clicksMap)) return;
      clicksMap[k] += 1;
      if (click.email) {
        uniqueEmailsByDay[k].add(click.email);
      }
    });
  });

  // Compose series mapped by label order
  const sent = labels.map((l) => sentMap[l]);
  const opened = labels.map((l) => openedMap[l]);
  const clicks = labels.map((l) => clicksMap[l]);
  const uniqueClicks = labels.map((l) => uniqueEmailsByDay[l].size);
  return { labels, sent, opened, clicks, uniqueClicks };
};

const buildChartDisplaySeries = (details) => {
  const ds = buildDailySeries(details);
  const activeDays = (ds.labels || [])
    .map((label, i) => ({
      label,
      sent: ds.sent?.[i] || 0,
      opened: ds.opened?.[i] || 0,
      clicks: ds.clicks?.[i] || 0,
      uniqueClicks: ds.uniqueClicks?.[i] || 0,
    }))
    .filter((row) => row.sent || row.opened || row.clicks || row.uniqueClicks);
  return {
    labels: activeDays.map((row) => row.label),
    sent: activeDays.map((row) => row.sent),
    opened: activeDays.map((row) => row.opened),
    clicks: activeDays.map((row) => row.clicks),
    uniqueClicks: activeDays.map((row) => row.uniqueClicks),
  };
};

const BarChart = ({ labels, series, colors, seriesNames, height = 300 }) => {
  // For each date, 4 separate bars for sent, opened, clicks, unique clickers
  const width = 800;
  const margin = { top: 40, right: 16, bottom: 64, left: 50 };
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;
  const groups = labels.length;
  const barsPerGroup = series.length; // Should be 4 now always
  // Find overall max value for y axis scaling
  const maxVal = Math.max(1, ...series.flat());
  const groupWidth = innerW / Math.max(1, groups);
  const barGap = Math.min(4, Math.max(1, groupWidth * 0.04));
  const barWidth = Math.max(2, (groupWidth - barGap * (barsPerGroup + 1)) / barsPerGroup);
  const y = (v) => innerH - (v / maxVal) * innerH;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block', maxWidth: '100%' }}
    >
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Y axis ticks and grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <g key={i}>
            <line 
              x1={0}
              x2={innerW}
              y1={innerH - innerH * t}
              y2={innerH - innerH * t}
              stroke="#e0e0e0"
              strokeWidth={0.5}
            />
            <text 
              x={-8}
              y={innerH - innerH * t}
              textAnchor="end"
              dominantBaseline="middle"
              fill="#666"
              fontSize={11}
              fontWeight={500}
            >
              {Math.round(maxVal * t)}
            </text>
          </g>
        ))}
        {/* Bars grouped by date */}
        {labels.map((label, gi) => {
          const groupX = gi * groupWidth;
          return (
            <g key={label}>
              {/* Draw 4 parallel bars: Sent (0), Opened (1), Clicks (2), Unique Clickers (3) */}
              {series.map((s, si) => {
                const val = (s && s[gi]) || 0;
                // Each bar space has some gap left and right, evenly space 4 bars.
                const barX = barGap + si * (barWidth + barGap);
                const yTop = y(val);
                const barHeight = innerH - yTop;
                // Show label above bar if value > 0
                return (
                  <g key={si}>
                    <rect
                      x={groupX + barX}
                      y={yTop}
                      width={barWidth}
                      height={Math.max(0, barHeight)}
                      fill={colors[si]}
                      rx={2}
                      opacity={0.85}
                      style={{ transition: 'opacity 0.2s' }}
                      onMouseEnter={e => e.target.setAttribute('opacity', '1')}
                      onMouseLeave={e => e.target.setAttribute('opacity', '0.85')}
                    />
                    {val > 0 && (
                      <text
                        x={groupX + barX + barWidth / 2}
                        y={yTop - 5}
                        textAnchor={barWidth < 10 ? 'start' : 'middle'}
                        fill="#333"
                        fontSize={8}
                        fontWeight={700}
                        transform={
                          barWidth < 10
                            ? `rotate(-90, ${groupX + barX + barWidth / 2}, ${yTop - 5})`
                            : undefined
                        }
                      >
                        {val}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* X axis date labels */}
              <text
                x={groupX + groupWidth / 2}
                y={innerH + 18}
                textAnchor="end"
                fill="#666"
                fontSize={10}
                fontWeight={500}
                transform={`rotate(-40, ${groupX + groupWidth / 2}, ${innerH + 18})`}
              >
                {formatChartLabel(label)}
              </text>
            </g>
          );
        })}
        {/* Y axis label */}
        <text
          x={-innerH / 2}
          y={-35}
          textAnchor="middle"
          transform="rotate(-90)"
          fill="#666"
          fontSize={12}
          fontWeight={600}
        >
          Count
        </text>
        {/* X axis label */}
        <text
          x={innerW / 2}
          y={innerH + 40}
          textAnchor="middle"
          fill="#666"
          fontSize={12}
          fontWeight={600}
        >
          Date
        </text>
        <line x1={0} x2={0} y1={0} y2={innerH} stroke="#333" strokeWidth={1.5} />
        <line x1={0} x2={innerW} y1={innerH} y2={innerH} stroke="#333" strokeWidth={1.5} />
      </g>
    </svg>
  );
};

const StatCard = ({ title, count, icon, delay = 0, onClick }) => (
  <Fade in={true} timeout={400 + delay}>
    <Paper
      elevation={1}
      onClick={onClick}
      sx={{
        p: 1.5,
        height: '100%',
        minHeight: { xs: '80px', sm: '85px', md: '90px' },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: '#ffffff',
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e9ecef',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          borderColor: '#0d6efd',
        },
      }}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        mb: 0,
      }}>
        <Typography
          variant="h2"
          component="div"
          sx={{
            fontWeight: 700,
            color: '#212529',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            lineHeight: 1.1,
            flex: 1,
          }}
        >
          <AnimatedCounter value={count} />
        </Typography>
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: 28, sm: 32, md: 36 },
            height: { xs: 28, sm: 32, md: 36 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
            color: 'white',
            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
            boxShadow: '0 2px 8px rgba(13, 110, 253, 0.3)',
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        variant="h6"
        sx={{
          color: '#6c757d',
          fontWeight: 500,
          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
          width: '100%',
        }}
      >
        {title}
      </Typography>
    </Paper>
  </Fade>
);

const Dashboard = ({ setActiveView }) => {
  const [stats, setStats] = useState({
    campaigns: 0,
    servers: 0,
    emailLists: 0,
    links: 0,
    contacts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignError, setCampaignError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [timelineTab, setTimelineTab] = useState(0);
  const [urlTab, setUrlTab] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [hidePdfHeader] = useState(false);
  const modalContentRef = useRef(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  useEffect(() => {
    setTimelineTab(0);
    setUrlTab(0);
  }, [campaignDetails]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setUserId(userData.id || null);
  }, []);

  useEffect(() => {
    fetchDashboardStats();
    fetchCampaignInfo();
    checkDuplicates();
  }, []);

  useEffect(() => {
    if (!loading && !error) {
      setTimeout(() => setLoaded(true), 100);
    }
  }, [loading, error]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/dashboard', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ 
          method: 'getInfo',
          user_id: userData.id || null,
          role_id: userData.role_id || null
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.status === '1' && data.data) {
        setStats({
          campaigns: data.data.campaigns || 0,
          servers: data.data.servers || 0,
          emailLists: data.data.emailLists || 0,
          links: data.data.links || 0,
          contacts: data.data.contacts || 0,
        });
      } else {
        throw new Error(data.error || 'Failed to load dashboard statistics');
      }
    } catch (error) {
      setError(error.message || 'Failed to load dashboard statistics');
      setStats({
        campaigns: 0,
        servers: 0,
        emailLists: 0,
        links: 0,
        contacts: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const checkDuplicates = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/members', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          method: 'check_duplicates_count',
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });
      if (!response.ok) throw new Error('Failed to check duplicates');
      const data = await response.json();
      if (data.status === '1' && data.data) {
        setDuplicateInfo(data.data);
      } else {
        setDuplicateInfo(null);
      }
    } catch (error) {
      setDuplicateInfo(null);
    }
  };

  const fetchCampaignInfo = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/dashboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          method: 'getCampaignInfo',
          user_id: userData.id || null,
          role_id: userData.role_id || null
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.status === '1' && Array.isArray(data.data)) {
        setCampaigns(data.data);
      } else {
        throw new Error(data.error || 'Failed to load campaign information');
      }
    } catch (err) {
      setCampaignError(err.message || 'Failed to load campaign information');
      setCampaigns([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: CHART_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const fetchCampaignDetails = async (campaignId) => {
    setDetailsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/dashboard', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ 
          method: 'getCampaignDetails', 
          campaign_id: campaignId,
          user_id: userData.id || null,
          role_id: userData.role_id || null
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCampaignDetails(data.data);
        setDetailsModalOpen(true);
      } else {
        throw new Error(data.error || 'Failed to load campaign details');
      }
    } catch (error) {
      alert(error.message || 'Failed to load campaign details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCampaignClick = (campaignId) => {
    setSelectedCampaign(campaignId);
    fetchCampaignDetails(campaignId);
  };

  // Rasterize an inline <svg> element to a high-resolution PNG data URL so the
  // chart stays perfectly crisp in the PDF at any zoom.
  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  };

  // Draw a grouped bar chart directly with jsPDF vector primitives (from the
  // same data as the on-screen chart) so it is sharp and never breaks.
  const drawBarChart = (pdf, ds, x, y, width, height) => {
    const colors = ['#42a5f5', '#66bb6a', '#ab47bc', '#ff7043'];
    const seriesNames = ['Sent', 'Opened', 'Clicks', 'Unique Clickers'];
    const series = [ds.sent, ds.opened, ds.clicks, ds.uniqueClicks];
    const labels = ds.labels || [];
    const m = { top: 22, right: 10, bottom: 34, left: 34 };
    const innerW = width - m.left - m.right;
    const innerH = height - m.top - m.bottom;
    const ox = x + m.left;
    const oy = y + m.top;
    const groups = labels.length;
    const barsPerGroup = series.length;
    const maxVal = Math.max(1, ...series.flat());
    const groupWidth = innerW / Math.max(1, groups);
    const barGap = Math.min(4, groupWidth * 0.04);
    const barWidth = Math.max(1, (groupWidth - barGap * (barsPerGroup + 1)) / barsPerGroup);
    const yPos = (v) => oy + innerH - (v / maxVal) * innerH;

    // Y axis gridlines + labels
    pdf.setDrawColor(224, 224, 224);
    pdf.setLineWidth(0.5);
    pdf.setFontSize(7);
    pdf.setTextColor(102, 102, 102);
    [0, 0.25, 0.5, 0.75, 1].forEach((t) => {
      const gy = oy + innerH - innerH * t;
      pdf.line(ox, gy, ox + innerW, gy);
      pdf.text(String(Math.round(maxVal * t)), ox - 4, gy + 2, { align: 'right' });
    });

    // Bars + value labels
    const showEveryXLabel = Math.max(1, Math.ceil(groups / 15));
    labels.forEach((label, gi) => {
      const groupX = ox + gi * groupWidth;
      series.forEach((s, si) => {
        const val = (s && s[gi]) || 0;
        if (val <= 0) return;
        const barX = groupX + barGap + si * (barWidth + barGap);
        const top = yPos(val);
        const [r, g, b] = hexToRgb(colors[si]);
        pdf.setFillColor(r, g, b);
        pdf.rect(barX, top, barWidth, oy + innerH - top, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(barWidth < 8 ? 5 : 6);
        pdf.setTextColor(51, 51, 51);
        pdf.text(String(val), barX + barWidth / 2, top - 2, { align: 'center' });
      });
      if (gi % showEveryXLabel === 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(102, 102, 102);
        pdf.text(formatChartLabel(label), groupX + groupWidth / 2, oy + innerH + 12, { align: 'center' });
      }
    });

    // Legend
    let legendX = ox;
    const legendY = oy + innerH + 26;
    pdf.setFontSize(8);
    seriesNames.forEach((name, si) => {
      const [r, g, b] = hexToRgb(colors[si]);
      pdf.setFillColor(r, g, b);
      pdf.rect(legendX, legendY - 6, 8, 8, 'F');
      pdf.setTextColor(73, 80, 87);
      pdf.text(name, legendX + 11, legendY);
      legendX += 11 + pdf.getTextWidth(name) + 16;
    });
  };

  const exportCampaignDetailsToPdf = async () => {
    if (!campaignDetails) return;
    setExportingPdf(true);
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 40;
      const contentWidth = pageWidth - marginX * 2;
      let cursorY = 48;

      const stats = campaignDetails.statistics || {};
      const campaign = campaignDetails.campaign || {};
      const campaignName = campaign.name || 'Campaign';
      const subject = campaign.subject || campaign.email_subject || campaign.title || 'N/A';

      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.setTextColor(33, 37, 41);
      pdf.text(campaignName, marginX, cursorY);
      cursorY += 20;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(108, 117, 125);
      const subjectLines = pdf.splitTextToSize(`Subject: ${subject}`, contentWidth);
      pdf.text(subjectLines, marginX, cursorY);
      cursorY += subjectLines.length * 14 + 8;

      // Daily activity chart (drawn as native vector graphics)
      const ds = buildChartDisplaySeries(campaignDetails);
      if (ds.labels && ds.labels.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setTextColor(33, 37, 41);
        pdf.text('Daily Activity Overview', marginX, cursorY);
        cursorY += 10;
        const chartHeight = 200;
        drawBarChart(pdf, ds, marginX, cursorY, contentWidth, chartHeight);
        cursorY += chartHeight + 20;

        // Daily activity table
        const dailyRows = (ds.labels || [])
          .map((label, i) => ({
            label,
            sent: ds.sent?.[i] || 0,
            opened: ds.opened?.[i] || 0,
            clicks: ds.clicks?.[i] || 0,
            uniqueClicks: ds.uniqueClicks?.[i] || 0,
          }))
          .filter((row) => row.sent || row.opened || row.clicks || row.uniqueClicks);
        if (dailyRows.length > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.setTextColor(33, 37, 41);
          pdf.text('Daily Report', marginX, cursorY);
          cursorY += 14;
          autoTable(pdf, {
            startY: cursorY,
            head: [['Date', 'Sent', 'Opened', 'Clicks', 'Unique Clickers']],
            body: dailyRows.map((row) => [
              formatChartLabel(row.label),
              String(row.sent),
              String(row.opened),
              String(row.clicks),
              String(row.uniqueClicks),
            ]),
            theme: 'striped',
            styles: { halign: 'center', fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [66, 165, 245], textColor: 255, fontStyle: 'bold' },
            columnStyles: { 0: { halign: 'left' } },
            margin: { left: marginX, right: marginX },
          });
          cursorY = pdf.lastAutoTable.finalY + 14;
        }
      }

      // Summary statistics table
      const sent = Number(stats.total_sent) || 0;
      const uniqueClickers = Number(stats.unique_clickers) || 0;
      const clickRate = sent > 0 ? `${((uniqueClickers / sent) * 100).toFixed(2)}%` : '0%';
      autoTable(pdf, {
        startY: cursorY,
        head: [['Submitted', 'Opened', 'Open Rate', 'Clicked', 'Click Rate', 'Unique Clickers']],
        body: [[
          String(stats.total_sent ?? 0),
          String(stats.total_opened ?? 0),
          `${stats.open_rate ?? 0}%`,
          String(stats.total_link_clicks ?? 0),
          clickRate,
          String(stats.unique_clickers ?? 0),
        ]],
        theme: 'grid',
        styles: { halign: 'center', fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [66, 165, 245], textColor: 255, fontStyle: 'bold' },
        margin: { left: marginX, right: marginX },
      });
      cursorY = pdf.lastAutoTable.finalY + 14;

      if (stats.start_date_time || stats.end_date_time) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(73, 80, 87);
        if (stats.start_date_time) {
          pdf.text(`Start Time: ${formatDate(stats.start_date_time)}`, marginX, cursorY);
          cursorY += 14;
        }
        if (stats.end_date_time) {
          pdf.text(`End Time: ${formatDate(stats.end_date_time)}`, marginX, cursorY);
          cursorY += 14;
        }
        cursorY += 6;
      }

      // Email sending timeline
      const sendTimeline = withOpenTimes(campaignDetails);
      if (sendTimeline.length > 0) {
        autoTable(pdf, {
          startY: cursorY,
          head: [[`Email Sending Timeline (${sendTimeline.length})`, 'Sent At', 'Opened', 'Open Count', 'Opened At']],
          body: sendTimeline.map((email) => [
            email.email || '-',
            formatDate(email.sent_at),
            email.opened ? 'Yes' : 'No',
            String(email.opened_count ?? 0),
            formatOpenedAt(email, formatDate),
          ]),
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak' },
          headStyles: { fillColor: [66, 165, 245], textColor: 255, fontStyle: 'bold' },
          margin: { left: marginX, right: marginX },
        });
        cursorY = pdf.lastAutoTable.finalY + 18;
      }

      // Link clicked timeline (one table per URL group)
      const clickTimeline = campaignDetails.click_timeline || [];
      clickTimeline.forEach((group) => {
        const url = group.url || 'Unknown URL';
        const clicks = group.clicks || [];
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(33, 37, 41);
        if (cursorY > pageHeight - 80) {
          pdf.addPage();
          cursorY = 48;
        }
        const urlLines = pdf.splitTextToSize(`Link: ${url}  (${group.click_count ?? clicks.length})`, contentWidth);
        pdf.text(urlLines, marginX, cursorY);
        cursorY += urlLines.length * 13 + 4;
        autoTable(pdf, {
          startY: cursorY,
          head: [['Email', 'Date Time']],
          body: clicks.map((entry) => [entry.email || '-', formatDate(entry.clicked_at)]),
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak' },
          headStyles: { fillColor: [171, 71, 188], textColor: 255, fontStyle: 'bold' },
          margin: { left: marginX, right: marginX },
        });
        cursorY = pdf.lastAutoTable.finalY + 18;
      });

      const sanitizedCampaignName = campaignName.replace(/[\\/:*?"<>|]/g, '_').slice(0, 25) || 'campaign';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `${sanitizedCampaignName}_details_${timestamp}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      alert('Failed to export campaign details as PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={50} thickness={4} />
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
          Loading dashboard statistics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            background: '#fff',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600 }}>
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const cardConfigs = [
    {
      title: 'Campaigns',
      count: stats.campaigns,
      icon: <FaChartLine />,
      delay: 0,
      onClick: () => setActiveView('campaigns'),
    },
    {
      title: 'Email Lists',
      count: stats.emailLists,
      icon: <FaList />,
      delay: 200,
      onClick: () => setActiveView('list'),
    },
    {
      title: 'Links',
      count: stats.links,
      icon: <FaLink />,
      delay: 300,
      onClick: () => setActiveView('links'),
    },
    {
      title: 'Servers',
      count: stats.servers,
      icon: <FaServer />,
      delay: 100,
      onClick: () => setActiveView('servers'),
    },
    {
      title: 'Contacts',
      count: stats.contacts,
      icon: <FaUsers />,
      delay: 400,
      onClick: () => setActiveView('members'),
    },
  ].filter((config) => {
    // Hide Links if user role is 2
    if (userRole === 2) {
      return config.title !== 'Links';
    }
    return true;
  });

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        background: '#f8f9fa',
        minHeight: 'calc(100vh - 56px)',
        width: '100%',
        overflowX: 'hidden',
      }}
    >
      <Fade in={true} timeout={600}>
        <Box>
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: '#212529',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              }}
            >
              Dashboard
            </Typography>
          </Box>
          {duplicateInfo && duplicateInfo.has_duplicates && (
            <Alert 
              severity="info" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => setActiveView('members')}
                  sx={{ fontWeight: 600 }}
                >
                  Go to Merge
                </Button>
              }
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Duplicate contact detected
              </Typography>
              <Typography variant="body2">
                Found {duplicateInfo.duplicate_groups} duplicate group(s) with {duplicateInfo.total_duplicate_records} duplicate record(s). 
                Please merge to maintain data integrity.
              </Typography>
            </Alert>
          )}

          <Grid 
            container 
            spacing={{ xs: 1.5, sm: 1.5, md: 2 }}
            sx={{
              mb: 3,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {cardConfigs.map((config, index) => (
              <Grid 
                item 
                key={index} 
                xs={12}
                sm={6}
                md={4}
                lg={2.4}
                sx={{
                  display: 'flex',
                  minWidth: 0,
                  width: '100%',
                  flexBasis: { lg: 'calc(20% - 16px)' },
                  maxWidth: { lg: 'calc(20% - 16px)' },
                }}
              >
                <StatCard {...config} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
            >
              Campaign Information
            </Typography>
            <TableContainer 
              component={Paper} 
              elevation={1}
              sx={{
                borderRadius: 2,
                border: '1px solid #e9ecef',
                overflowX: 'auto',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#212529' }}>Campaign Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#212529' }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#212529' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#212529' }}>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No campaigns found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    campaigns.map((c) => (
                      <TableRow key={c.id} hover
                        sx={{
                          '&:hover': { backgroundColor: '#f8f9fa' }
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              cursor: 'pointer',
                              color: '#0d6efd',
                              fontWeight: 500,
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#0b5ed7',
                              },
                            }}
                            onClick={() => handleCampaignClick(c.id)}
                          >
                            {c.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: '#495057' }}>{c.subject}</TableCell>
                        <TableCell sx={{ color: '#495057' }}>{c.status || 'N/A'}</TableCell>
                        <TableCell sx={{ color: '#6c757d' }}>{formatDate(c.created_at || c.created_at_timestamp)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>

      {/* Campaign Details Modal */}
      <Modal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setCampaignDetails(null);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            width: { xs: '95%', sm: '90%', md: '85%' },
            maxWidth: { xs: '95%', sm: '90%', md: '1200px' },
            maxHeight: exportingPdf ? 'none' : '90vh',
            overflow: exportingPdf ? 'visible' : 'auto',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid #e9ecef',
          }}
        >
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : campaignDetails ? (
            <Box ref={modalContentRef}>
              <Box
                sx={{
                  display: hidePdfHeader ? 'none' : 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Campaign Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<FaFilePdf />}
                    onClick={exportCampaignDetailsToPdf}
                    disabled={exportingPdf}
                  >
                    {exportingPdf ? 'Rendering…' : 'Download PDF'}
                  </Button>
                  <IconButton
                    onClick={() => {
                      setDetailsModalOpen(false);
                      setCampaignDetails(null);
                    }}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <FaTimes />
                  </IconButton>
                </Box>
              </Box>

              {campaignDetails.campaign && (
                <Box
                  sx={{
                    display: hidePdfHeader ? 'block' : 'none',
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {campaignDetails.campaign.name || 'Campaign'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Subject:{' '}
                    {campaignDetails.campaign.subject ||
                      campaignDetails.campaign.email_subject ||
                      campaignDetails.campaign.title ||
                      'N/A'}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Daily Activity Overview (Asia/Kolkata)
                </Typography>
                {(() => {
                  const chartDs = buildChartDisplaySeries(campaignDetails);
                  const activeDays = (chartDs.labels || []).map((label, i) => ({
                    label,
                    sent: chartDs.sent?.[i] || 0,
                    opened: chartDs.opened?.[i] || 0,
                    clicks: chartDs.clicks?.[i] || 0,
                    uniqueClicks: chartDs.uniqueClicks?.[i] || 0,
                  }));
                  return (
                    <Box>
                      <Paper sx={{ 
                        p: { xs: 1.5, sm: 2 }, 
                        bgcolor: '#ffffff',
                        border: '1px solid #e9ecef',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}>
                          <BarChart
                            labels={chartDs.labels}
                            series={[chartDs.sent, chartDs.opened, chartDs.clicks, chartDs.uniqueClicks]}
                            colors={["#42a5f5", "#66bb6a", "#ab47bc", "#ff7043"]}
                            seriesNames={["Sent", "Opened", "Clicks", "Unique Clickers"]}
                            height={340}
                          />
                      </Paper>
                      <TableContainer sx={{ mt: 2, maxHeight: 240 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Sent</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Opened</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Clicks</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Unique Clickers</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {activeDays.map((row) => (
                              <TableRow key={row.label}>
                                <TableCell>{formatChartLabel(row.label)}</TableCell>
                                <TableCell align="right">{row.sent}</TableCell>
                                <TableCell align="right">{row.opened}</TableCell>
                                <TableCell align="right">{row.clicks}</TableCell>
                                <TableCell align="right">{row.uniqueClicks}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#42a5f5', borderRadius: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Sent Count</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#66bb6a', borderRadius: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Opened Count</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#ab47bc', borderRadius: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Click Count ({campaignDetails.statistics.total_link_clicks})
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#ff7043', borderRadius: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Unique Clickers ({campaignDetails.statistics.unique_clickers})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Paper sx={{ 
                      p: 2, textAlign: 'center', bgcolor: '#ffffff',
                      border: '1px solid #e9ecef', borderRadius: 2,
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                        {campaignDetails.statistics.total_sent}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                        Submitted
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Paper sx={{ 
                      p: 2, textAlign: 'center', bgcolor: '#ffffff',
                      border: '1px solid #e9ecef', borderRadius: 2,
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                        {campaignDetails.statistics.total_opened}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                        Opened
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Paper sx={{ 
                      p: 2, textAlign: 'center', bgcolor: '#ffffff',
                      border: '1px solid #e9ecef', borderRadius: 2,
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                        {campaignDetails.statistics.open_rate}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                        Open Rate
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Paper sx={{ 
                      p: 2, textAlign: 'center', bgcolor: '#ffffff',
                      border: '1px solid #e9ecef', borderRadius: 2,
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                        {campaignDetails.statistics.total_link_clicks}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                        Clicked
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Paper sx={{ 
                      p: 2, textAlign: 'center', bgcolor: '#ffffff',
                      border: '1px solid #e9ecef', borderRadius: 2,
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                        {(() => {
                          const sent = Number(campaignDetails.statistics.total_sent) || 0;
                          const uniqueClickers = Number(campaignDetails.statistics.unique_clickers) || 0;
                          if (sent <= 0) return '0%';
                          const rate = (uniqueClickers / sent) * 100;
                          return `${rate.toFixed(2)}%`;
                        })()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                        Click Rate
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Paper sx={{ 
                      p: 2, textAlign: 'center', bgcolor: '#ffffff',
                      border: '1px solid #e9ecef', borderRadius: 2,
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#212529' }}>
                        {campaignDetails.statistics.unique_clickers}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                        Unique Clickers
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {campaignDetails.statistics.start_date_time && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Start Time:</Typography>
                      <Typography variant="body1">
                        {formatDate(campaignDetails.statistics.start_date_time)}
                      </Typography>
                    </Grid>
                  )}
                  {campaignDetails.statistics.end_date_time && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">End Time:</Typography>
                      <Typography variant="body1">
                        {formatDate(campaignDetails.statistics.end_date_time)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Divider sx={{ my: 3 }} />
              {(campaignDetails.send_timeline.length > 0 || campaignDetails.click_timeline.length > 0) && (
                <Box>
                  <Tabs
                    value={timelineTab}
                    onChange={(e, v) => setTimelineTab(v)}
                    sx={{ mb: 2 }}
                  >
                    {campaignDetails.send_timeline.length > 0 && (
                      <Tab label={`Email Sending Timeline (${campaignDetails.send_timeline.length})`} />
                    )}
                    {campaignDetails.click_timeline.length > 0 && (
                      <Tab label={`Link Clicked Timeline (${campaignDetails.click_timeline.length})`} />
                    )}
                  </Tabs>
                  {campaignDetails.send_timeline.length > 0 && timelineTab === 0 && (
                    <Box sx={{ mb: 3 }}>
                      <TableContainer sx={{ maxHeight: exportingPdf ? 'none' : 240, overflow: exportingPdf ? 'visible' : 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Sent At</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Opened</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Open Count</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Opened At</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {withOpenTimes(campaignDetails).map((email, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{email.email}</TableCell>
                                <TableCell>{formatDate(email.sent_at)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={email.opened ? 'Yes' : 'No'}
                                    color={email.opened ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{email.opened_count}</TableCell>
                                <TableCell>
                                  {email.opened_at ? (
                                    <>
                                      {formatDate(email.opened_at)}
                                      {email.last_opened_at && email.last_opened_at !== email.opened_at && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                          Last: {formatDate(email.last_opened_at)}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                  {campaignDetails.click_timeline.length > 0 && (
                    <Box sx={{ display: timelineTab === (campaignDetails.send_timeline.length > 0 ? 1 : 0) ? 'block' : 'none' }}>
                      <Tabs
                        value={Math.min(urlTab, Math.max(0, campaignDetails.click_timeline.length - 1))}
                        onChange={(e, v) => setUrlTab(v)}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        {campaignDetails.click_timeline.map((group, idx) => (
                          <Tab
                            key={idx}
                            label={`${(group.url || 'Unknown URL').length > 40 ? (group.url || 'Unknown URL').slice(0, 40) + '…' : (group.url || 'Unknown URL')} (${group.click_count})`}
                          />
                        ))}
                      </Tabs>
                      {campaignDetails.click_timeline[urlTab] && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography
                              component="a"
                              href={campaignDetails.click_timeline[urlTab].url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                                wordBreak: 'break-word'
                              }}
                            >
                              {campaignDetails.click_timeline[urlTab].url || 'Unknown URL'}
                            </Typography>
                            <Chip label={campaignDetails.click_timeline[urlTab].click_count} color="primary" size="small" sx={{ fontWeight: 600 }} />
                          </Box>
                          <TableContainer sx={{ maxHeight: exportingPdf ? 'none' : 300, overflow: exportingPdf ? 'visible' : 'auto' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Date Time</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {campaignDetails.click_timeline[urlTab].clicks.map((entry, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{entry.email || '-'}</TableCell>
                                    <TableCell>{formatDate(entry.clicked_at)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : null}
        </Paper>
      </Modal>
    </Box>
  );
};

export default Dashboard;
